/**
 * QuoteRequestFormHuman.jsx — Coaching Humain — API: POST /api/enterprise/quote-requests/human
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import BackButton from '../../components/BackButton';
import {
  Users, ArrowRight, ArrowLeft, Check, Loader2, Target,
  Building2, Sparkles, Briefcase, CheckCircle2, ShieldCheck, Crown,
  Gift,
} from 'lucide-react';

const API = getApiUrl();

const SPECIALTIES = [
  'Leadership', 'Management', 'Communication', 'Gestion du stress',
  'Prise de parole', 'Intelligence émotionnelle', 'Gestion de conflits',
  'Transition de carrière', 'Performance', 'Organisation',
  "Coaching d'équipe", 'Développement personnel',
];
const COMPANY_SIZES = [
  { value: 'tpe', label: 'TPE', hint: '<10 salariés' },
  { value: 'pme', label: 'PME', hint: '10-250 salariés' },
  { value: 'eti', label: 'ETI', hint: '250-5000' },
  { value: 'ge', label: 'Grand groupe', hint: '5000+' },
];
const MANAGER_TYPES = [
  { value: 'n_minus_1', label: 'N-1' },
  { value: 'middle', label: 'Middle management' },
  { value: 'top', label: 'Top management' },
  { value: 'executive', label: 'Executive / Comex' },
];
const BUDGETS = [
  { value: '5k_15k', label: '5 k€ – 15 k€' },
  { value: '15k_50k', label: '15 k€ – 50 k€' },
  { value: '50k_150k', label: '50 k€ – 150 k€' },
  { value: '150k_plus', label: '150 k€+' },
];
const DURATIONS = [3, 6, 12, 18, 24];
const COACH_LEVELS = [
  { value: 'certifie', label: 'Certifié', hint: 'Entry-level' },
  { value: 'senior', label: 'Senior', hint: '10+ années' },
  { value: 'executive', label: 'Executive', hint: 'Coachs de dirigeants' },
];

export default function QuoteRequestFormHuman() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [contact, setContact] = useState({
    company_name: '', company_size: '', vat_number: '', billing_address: '',
    hr_contact_name: user?.name || '', hr_contact_email: user?.email || '', hr_contact_phone: '',
  });
  const [payload, setPayload] = useState({
    collaborators_count: 15, manager_typologies: [],
    business_objectives: '', objectives_tags: [],
    estimated_budget_eur: null, budget_range: '',
    engagement_duration_months: 6, coach_requirement_level: '',
    executives_count: null, executive_coach_mandatory: null, confidentiality_nda_required: null,
    specialties_focus: [], notes: '',
    // P0 Iter237 — Options COACHDIA Prism (optionnelles, sans prix affiché)
    insights_options: { individual_included: false, collective_included: false, notes: '' },
  });

  const showExec = payload.manager_typologies.includes('executive') || payload.coach_requirement_level === 'executive';

  const toggleMT = (v) => setPayload((p) => ({
    ...p, manager_typologies: p.manager_typologies.includes(v)
      ? p.manager_typologies.filter(x => x !== v) : [...p.manager_typologies, v]
  }));
  const toggleSp = (s) => setPayload((p) => ({
    ...p, specialties_focus: p.specialties_focus.includes(s)
      ? p.specialties_focus.filter(x => x !== s) : [...p.specialties_focus, s]
  }));

  const stepValid = useMemo(() => {
    if (step === 1) return contact.company_name.trim().length >= 2 && contact.company_size
      && contact.hr_contact_name.trim().length >= 2
      && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.hr_contact_email);
    if (step === 2) return payload.collaborators_count > 0
      && payload.manager_typologies.length >= 1 && payload.coach_requirement_level;
    if (step === 3) {
      const base = payload.business_objectives.trim().length >= 10
        && payload.budget_range && payload.engagement_duration_months > 0;
      if (!base) return false;
      if (showExec) return payload.executives_count !== null && payload.executives_count >= 0
        && payload.executive_coach_mandatory !== null && payload.confidentiality_nda_required !== null;
      return true;
    }
    if (step === 4) return true;   // Étape Options COACHDIA Prism : toujours valide (optionnelle)
    return true;
  }, [step, contact, payload, showExec]);

  const handleSubmit = async () => {
    if (!user) { toast.error('Veuillez vous connecter'); navigate('/login'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/enterprise/quote-requests/human`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contact, payload }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la soumission');
      }
      const data = await res.json();
      navigate(`/subscription/enterprise/quote-request/success?id=${data.id}&type=human`);
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  const steps = ['Votre entreprise', 'Population & niveau', 'Engagement', 'Options COACHDIA Prism', 'Récapitulatif'];

  return (
    <div className="theme-light min-h-screen pb-28 sm:pb-12 bg-[#F5FBF7]">
      <div className="border-b border-[#D9F2E4] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-6">
          <BackButton />
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Users className="w-4 h-4 text-[#059669]" />
            <p className="uppercase text-[11px] tracking-[0.25em] text-[#059669] font-medium">
              Demande de devis — Coaching Humain
            </p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] tracking-tight">
            Un accompagnement à la hauteur de vos enjeux
          </h1>
          <p className="text-[#4B5563] text-sm mt-2">Réponse sous 48 h ouvrées.</p>

          <div className="mt-6 flex items-center gap-2" data-testid="qreq-human-steps">
            {steps.map((label, idx) => {
              const n = idx + 1; const active = n === step; const done = n < step;
              return (
                <div key={label} className="flex-1 flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium transition-all ${
                    done ? 'bg-[#047857] text-white' : active ? 'bg-[#059669] text-white' : 'bg-[#EBEAE3] text-[#6B7280]'
                  }`}>
                    {done ? <Check className="w-3.5 h-3.5" /> : n}
                  </div>
                  <span className={`text-[11px] ${active ? 'text-[#111827] font-medium' : 'text-[#6B7280]'} hidden sm:inline`}>{label}</span>
                  {n < steps.length && <div className={`flex-1 h-px ${done ? 'bg-[#047857]' : 'bg-[#E8E5DE]'}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.2 }} className="space-y-5">
            {step === 1 && (
              <>
                <Section title="Informations entreprise" icon={Building2}>
                  <Field label="Nom de l'entreprise *">
                    <Input data-testid="qreq-human-company-name" value={contact.company_name}
                      onChange={(e) => setContact({ ...contact, company_name: e.target.value })}
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                  <div>
                    <label className="text-xs text-[#4B5563] mb-2 block">Taille *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {COMPANY_SIZES.map((s) => (
                        <button key={s.value} type="button" data-testid={`qreq-human-size-${s.value}`}
                          onClick={() => setContact({ ...contact, company_size: s.value })}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            contact.company_size === s.value ? 'border-[#059669] bg-[#ECFDF5]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                          }`}>
                          <div className="text-sm font-medium text-[#111827]">{s.label}</div>
                          <div className="text-[10px] text-[#6B7280] mt-0.5">{s.hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Adresse de facturation">
                      <Input data-testid="qreq-human-billing-address" value={contact.billing_address}
                        onChange={(e) => setContact({ ...contact, billing_address: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                    <Field label="N° TVA">
                      <Input data-testid="qreq-human-vat" value={contact.vat_number}
                        onChange={(e) => setContact({ ...contact, vat_number: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                  </div>
                </Section>
                <Section title="Contact RH" icon={Users}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Nom *">
                      <Input data-testid="qreq-human-contact-name" value={contact.hr_contact_name}
                        onChange={(e) => setContact({ ...contact, hr_contact_name: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                    <Field label="E-mail *">
                      <Input data-testid="qreq-human-contact-email" type="email" value={contact.hr_contact_email}
                        onChange={(e) => setContact({ ...contact, hr_contact_email: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                    <Field label="Téléphone">
                      <Input data-testid="qreq-human-contact-phone" value={contact.hr_contact_phone}
                        onChange={(e) => setContact({ ...contact, hr_contact_phone: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                  </div>
                </Section>
              </>
            )}

            {step === 2 && (
              <>
                <Section title="Collaborateurs concernés" icon={Users}>
                  <Field label="Nombre total *">
                    <Input data-testid="qreq-human-collab-count" type="number" min="1"
                      value={payload.collaborators_count}
                      onChange={(e) => setPayload({ ...payload, collaborators_count: parseInt(e.target.value) || 0 })}
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                </Section>
                <Section title="Typologies managériales *" icon={Briefcase}>
                  <div className="grid grid-cols-2 gap-2">
                    {MANAGER_TYPES.map((t) => (
                      <button key={t.value} type="button" data-testid={`qreq-human-type-${t.value}`}
                        onClick={() => toggleMT(t.value)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          payload.manager_typologies.includes(t.value) ? 'border-[#059669] bg-[#ECFDF5]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                        }`}>
                        <div className="text-sm font-medium text-[#111827] flex items-center gap-1.5">
                          {payload.manager_typologies.includes(t.value) && <Check className="w-3.5 h-3.5 text-[#059669]" />}
                          {t.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </Section>
                <Section title="Niveau d'exigence coach *" icon={Crown}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {COACH_LEVELS.map((l) => (
                      <button key={l.value} type="button" data-testid={`qreq-human-coach-level-${l.value}`}
                        onClick={() => setPayload({ ...payload, coach_requirement_level: l.value })}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          payload.coach_requirement_level === l.value ? 'border-[#059669] bg-[#ECFDF5]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                        }`}>
                        <div className="text-sm font-medium text-[#111827]">{l.label}</div>
                        <div className="text-[11px] text-[#6B7280] mt-0.5">{l.hint}</div>
                      </button>
                    ))}
                  </div>
                </Section>
              </>
            )}

            {step === 3 && (
              <>
                <Section title="Objectifs business *" icon={Target}>
                  <Textarea data-testid="qreq-human-objectives" value={payload.business_objectives}
                    onChange={(e) => setPayload({ ...payload, business_objectives: e.target.value })}
                    placeholder="Décrivez les enjeux business, les transformations souhaitées..."
                    className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl min-h-[120px]" />
                </Section>
                <Section title="Spécialités souhaitées" icon={Sparkles}>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALTIES.map((s) => (
                      <button key={s} type="button"
                        data-testid={`qreq-human-specialty-${s.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        onClick={() => toggleSp(s)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          payload.specialties_focus.includes(s) ? 'bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]' : 'bg-white text-[#4B5563] border border-[#E8E5DE] hover:border-[#DDD9D0]'
                        }`}>
                        {payload.specialties_focus.includes(s) && <Check className="w-3 h-3 inline mr-1" />}
                        {s}
                      </button>
                    ))}
                  </div>
                </Section>
                <Section title="Budget & engagement" icon={Building2}>
                  <div>
                    <label className="text-xs text-[#4B5563] mb-2 block">Budget cible *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {BUDGETS.map((b) => (
                        <button key={b.value} type="button" data-testid={`qreq-human-budget-${b.value}`}
                          onClick={() => setPayload({ ...payload, budget_range: b.value })}
                          className={`p-3 rounded-xl border text-center transition-all ${
                            payload.budget_range === b.value ? 'border-[#059669] bg-[#ECFDF5]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                          }`}>
                          <div className="text-sm font-medium text-[#111827]">{b.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="text-xs text-[#4B5563] mb-2 block">Durée engagement (mois) *</label>
                    <div className="flex flex-wrap gap-2">
                      {DURATIONS.map((d) => (
                        <button key={d} type="button" data-testid={`qreq-human-duration-${d}`}
                          onClick={() => setPayload({ ...payload, engagement_duration_months: d })}
                          className={`px-4 py-2 rounded-full border text-sm transition-all ${
                            payload.engagement_duration_months === d ? 'border-[#059669] bg-[#ECFDF5] text-[#047857] font-medium' : 'border-[#E8E5DE] bg-white text-[#4B5563]'
                          }`}>{d} mois</button>
                      ))}
                    </div>
                  </div>
                  <Field label="Budget estimatif précis (EUR)">
                    <Input data-testid="qreq-human-budget-exact" type="number" min="0"
                      value={payload.estimated_budget_eur ?? ''}
                      onChange={(e) => setPayload({ ...payload, estimated_budget_eur: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      placeholder="Optionnel"
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                </Section>

                {showExec && (
                  <div className="bg-white border border-[#E8DFC8] ring-1 ring-[#D4AF37]/30 rounded-2xl shadow-sm p-5" data-testid="qreq-human-executive-block">
                    <h3 className="text-[#111827] font-display font-medium mb-4 flex items-center gap-2 text-sm">
                      <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                      Exigences executives (niveau renforcé)
                    </h3>
                    <div className="space-y-3">
                      <Field label="Nombre de dirigeants concernés *">
                        <Input data-testid="qreq-human-executives-count" type="number" min="0"
                          value={payload.executives_count ?? ''}
                          onChange={(e) => setPayload({ ...payload, executives_count: e.target.value === '' ? null : parseInt(e.target.value) })}
                          className="bg-white border-[#DDD9D0] rounded-xl" />
                      </Field>
                      <YesNoField testid="qreq-human-exec-mandatory"
                        label="Coach certifié executive-level impératif ?"
                        value={payload.executive_coach_mandatory}
                        onChange={(v) => setPayload({ ...payload, executive_coach_mandatory: v })} />
                      <YesNoField testid="qreq-human-nda"
                        label="Signature d'un NDA contractuel requise ?"
                        value={payload.confidentiality_nda_required}
                        onChange={(v) => setPayload({ ...payload, confidentiality_nda_required: v })} />
                    </div>
                  </div>
                )}

                <Section title="Notes complémentaires" icon={Sparkles}>
                  <Textarea data-testid="qreq-human-notes" value={payload.notes}
                    onChange={(e) => setPayload({ ...payload, notes: e.target.value })}
                    placeholder="Contexte, contraintes..."
                    className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl min-h-[90px]" />
                </Section>
              </>
            )}

            {step === 4 && (
              <>
                <Section title="Options COACHDIA Prism (optionnel)" icon={Gift}>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Enrichissez le coaching humain avec nos modules Insights. L'admin CoachDIA vous enverra le chiffrage détaillé avec le devis final (tarification existante, aucun coût caché).
                  </p>
                  <div className="grid grid-cols-1 gap-3 mt-2">
                    <OptionToggle
                      testid="qreq-human-insights-individual"
                      active={payload.insights_options.individual_included}
                      onClick={() => setPayload((p) => ({
                        ...p,
                        insights_options: {
                          ...p.insights_options,
                          individual_included: !p.insights_options.individual_included,
                        },
                      }))}
                      title="COACHDIA Prism pour chaque collaborateur"
                      description="Chaque collaborateur inclus bénéficie d'un test comportemental premium (~30 pages) sans paiement personnel. Rapport PDF, profil d'énergies, plan 30/60/90 jours."
                    />
                    <OptionToggle
                      testid="qreq-human-insights-collective"
                      active={payload.insights_options.collective_included}
                      onClick={() => setPayload((p) => ({
                        ...p,
                        insights_options: {
                          ...p.insights_options,
                          collective_included: !p.insights_options.collective_included,
                        },
                      }))}
                      title="COACHDIA Pulse / COACHDIA Pulse"
                      description="1 analyse d'équipe avec cartographie, synergies, frictions, scripts managériaux. Lancement dès 3 profils individuels complétés."
                    />
                  </div>
                  <Field label="Notes COACHDIA Prism (optionnel)">
                    <Textarea
                      data-testid="qreq-human-insights-notes"
                      value={payload.insights_options.notes || ''}
                      onChange={(e) => setPayload((p) => ({
                        ...p,
                        insights_options: { ...p.insights_options, notes: e.target.value },
                      }))}
                      placeholder="Précisez un besoin COACHDIA Prism spécifique..."
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl min-h-[80px]"
                    />
                  </Field>
                </Section>
              </>
            )}

            {step === 5 && (
              <Section title="Récapitulatif avant envoi" icon={CheckCircle2}>
                <SummaryRow label="Entreprise" value={contact.company_name} />
                <SummaryRow label="Taille" value={COMPANY_SIZES.find(s => s.value === contact.company_size)?.label} />
                <SummaryRow label="Contact RH" value={`${contact.hr_contact_name} · ${contact.hr_contact_email}`} />
                <SummaryRow label="Collaborateurs" value={payload.collaborators_count} />
                <SummaryRow label="Typologies" value={payload.manager_typologies.map(t => MANAGER_TYPES.find(m => m.value === t)?.label).join(', ')} />
                <SummaryRow label="Niveau coach" value={COACH_LEVELS.find(l => l.value === payload.coach_requirement_level)?.label} />
                <SummaryRow label="Budget" value={BUDGETS.find(b => b.value === payload.budget_range)?.label} />
                <SummaryRow label="Durée" value={`${payload.engagement_duration_months} mois`} />
                <SummaryRow label="Spécialités" value={payload.specialties_focus.join(', ') || '—'} />
                <SummaryRow
                  label="COACHDIA Prism"
                  value={
                    (payload.insights_options.individual_included || payload.insights_options.collective_included)
                      ? [
                          payload.insights_options.individual_included ? 'Individuel' : null,
                          payload.insights_options.collective_included ? 'Collectif' : null,
                        ].filter(Boolean).join(' + ')
                      : 'Aucun'
                  }
                />
                {showExec && (
                  <>
                    <SummaryRow label="Dirigeants" value={payload.executives_count} />
                    <SummaryRow label="Coach exec impératif" value={boolLabel(payload.executive_coach_mandatory)} />
                    <SummaryRow label="NDA requis" value={boolLabel(payload.confidentiality_nda_required)} />
                  </>
                )}
                {payload.notes && <SummaryRow label="Notes" value={payload.notes} />}
              </Section>
            )}

            <div className="flex items-center justify-between pt-4">
              <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                data-testid="qreq-human-prev"
                className="px-5 py-2.5 rounded-full border border-[#E8E5DE] bg-white text-[#4B5563] text-sm font-medium hover:bg-[#F8F7F4] disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Précédent
              </button>
              {step < 5 ? (
                <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!stepValid}
                  data-testid="qreq-human-next"
                  className="px-5 py-2.5 rounded-full bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2">
                  Suivant <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting}
                  data-testid="qreq-human-submit"
                  className="px-6 py-2.5 rounded-full bg-[#059669] text-white text-sm font-display font-semibold hover:bg-[#047857] disabled:opacity-50 transition-all inline-flex items-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Envoyer ma demande de devis
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E8E5DE] rounded-2xl shadow-sm p-5">
      <h3 className="text-[#111827] font-display font-medium mb-4 flex items-center gap-2 text-sm">
        {Icon && <Icon className="w-4 h-4 text-[#059669]" />}
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#4B5563] mb-1.5 block font-medium">{label}</label>
      {children}
    </div>
  );
}
function YesNoField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-[#4B5563]">{label}</span>
      <div className="flex gap-2">
        {[{ v: true, l: 'Oui' }, { v: false, l: 'Non' }].map((opt) => (
          <button key={opt.l} type="button"
            data-testid={`${testid}-${opt.l.toLowerCase()}`}
            onClick={() => onChange(opt.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              value === opt.v ? 'bg-[#111827] text-white' : 'bg-white border border-[#E8E5DE] text-[#4B5563]'
            }`}>{opt.l}</button>
        ))}
      </div>
    </div>
  );
}
function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#F0EEE8] last:border-0">
      <span className="text-xs text-[#6B7280] uppercase tracking-wider">{label}</span>
      <span className="text-sm text-[#111827] text-right max-w-[60%]">{value || '—'}</span>
    </div>
  );
}
function boolLabel(v) { if (v === true) return 'Oui'; if (v === false) return 'Non'; return '—'; }

function OptionToggle({ active, onClick, title, description, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={`text-left p-4 rounded-2xl border transition-all ${
        active
          ? 'border-[#059669] bg-[#ECFDF5] ring-1 ring-[#059669]/30'
          : 'border-[#E8E5DE] bg-white hover:border-[#059669]/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 shrink-0 mt-0.5 rounded-md border-2 flex items-center justify-center ${
          active ? 'border-[#059669] bg-[#059669]' : 'border-[#D1D5DB] bg-white'
        }`}>
          {active && <Check className="w-3 h-3 text-white" />}
        </div>
        <div className="flex-1">
          <div className="text-sm font-display font-medium text-[#111827]">{title}</div>
          <div className="text-xs text-[#4B5563] mt-1 leading-relaxed">{description}</div>
        </div>
      </div>
    </button>
  );
}
